import { FC } from 'react'
import { useTranslation } from 'next-i18next'

import PriceFilter from './PriceFilter'
import BedBathFilter from './BedBathFilter'
import MobileDialog from './MobileDialog'
import LocationSearch from './LocationSearch'
import FavoriteToggle from './FavoriteToggle'

import { Dropdown } from 'ui'

import {
  SortByState,
  useListingsState,
} from '../../services/context/ListingsContext'
import { useTracking } from '../../services/context/TrackingContext'
import { MarketFilter } from './MarketFilter'
import { useMarketParam } from '../../hooks/use-market-param'

type StandardSearchProperties = {
  search_term: string
  min_list_price: number | null
  max_list_price: number | null
  min_bedroom_count: number | null
  min_bathroom_count: number | null
}

export interface ISortByMethodOption {
  label: string
  value: SortByState
}

export const trackingSortTarget: Record<SortByState, string> = {
  price_high_to_low: 'Listing Price',
  price_low_to_high: 'Listing Price',
  time_on_market_asc: 'Time on Market',
  time_on_market_desc: 'Time on Market',
}

export const useTrackingPriceFilter = () => {
  const { track } = useTracking()

  return (
    min: number | null,
    max: number | null,
    standardSearchProperties: StandardSearchProperties
  ) => {
    track({
      event: 'Property Search Results Filtered',
      properties: {
        ...standardSearchProperties,
        min_list_price: min,
        max_list_price: max,
        filter_method: 'Multiple Values',
        filter_target: 'Purchase Price',
      },
    })
  }
}

export const useTrackingBedBathFilter = () => {
  const { track } = useTracking()

  return (
    beds: number | null,
    baths: number | null,
    standardSearchProperties: StandardSearchProperties
  ) => {
    track({
      event: 'Property Search Results Filtered',
      properties: {
        ...standardSearchProperties,
        min_bedroom_count: beds,
        min_bathroom_count: baths,
        filter_method: 'Multiple Values',
        filter_target: 'Beds & Baths',
      },
    })
  }
}

export const useTrackingSort = () => {
  const { track } = useTracking()

  return (
    sortByState: SortByState,
    standardSearchProperties: StandardSearchProperties
  ) => {
    track({
      event: 'Property Search Results Sorted',
      properties: {
        ...standardSearchProperties,
        sort_method: 'Dropdown',
        sort_target: trackingSortTarget[sortByState],
        sort_direction: ['price_high_to_low', 'time_on_market_desc'].includes(
          sortByState
        )
          ? 'descending'
          : 'ascending',
      },
    })
  }
}

const FilterBar: FC = () => {
  const { t } = useTranslation('common')
  const { track } = useTracking()
  const trackPriceFilter = useTrackingPriceFilter()
  const trackBedBathsFilter = useTrackingBedBathFilter()
  const trackSort = useTrackingSort()

  const {
    state,
    routePushAddressFilter,
    routePushPriceFilter,
    routePushBedBathFilter,
    routePushSortMethod,
    routePushFavoritesFilter,
    routePushMarket,
  } = useListingsState()
  const {
    priceMin,
    priceMax,
    bedsMin,
    bathsMin,
    sortByMethod,
    address,
    favorites,
  } = state

  const market = useMarketParam()

  const sortByMethodOptions: ISortByMethodOption[] = [
    { label: t('ui.time-on-market-asc'), value: 'time_on_market_asc' },
    { label: t('ui.time-on-market-desc'), value: 'time_on_market_desc' },
    { label: t('ui.price-high-to-low'), value: 'price_high_to_low' },
    { label: t('ui.price-low-to-high'), value: 'price_low_to_high' },
  ]

  const formatStandardSearchProperties = () => ({
    search_term: address,
    min_list_price: priceMin,
    max_list_price: priceMax,
    min_bedroom_count: bedsMin,
    min_bathroom_count: bathsMin,
  })

  return (
    <form
      className="relative z-10 bg-white shadow-lg"
      data-testid="filter-form"
      onSubmit={(e) => {
        e.preventDefault()
      }}
    >
      <div className="mx-auto flex max-w-1440 items-center justify-between px-24 pb-12 pt-16 lg:px-48">
        <div className="flex items-center">
          <ul className="mr-16 flex space-x-16 lg:mr-0">
            <li>
              <LocationSearch
                address={address}
                onDone={(address) => {
                  routePushAddressFilter(address)

                  if (address) {
                    track({
                      event: 'Property Search Run',
                      properties: {
                        ...formatStandardSearchProperties(),
                        search_term: address,
                      },
                    })
                  }
                }}
              />
            </li>
            <li className="lg-d:hidden">
              <MarketFilter
                market={market}
                onDoneButtonClick={routePushMarket}
              />
            </li>
            <li className="lg-d:hidden">
              <PriceFilter
                min={priceMin}
                max={priceMax}
                onDone={(min, max) => {
                  routePushPriceFilter(min, max)
                  const standardSearchProperties =
                    formatStandardSearchProperties()
                  trackPriceFilter(min, max, standardSearchProperties)
                }}
              />
            </li>
            <li className="lg-d:hidden">
              <BedBathFilter
                beds={bedsMin}
                baths={bathsMin}
                onDone={(beds, baths) => {
                  routePushBedBathFilter(beds, baths)
                  const standardSearchProperties =
                    formatStandardSearchProperties()
                  trackBedBathsFilter(beds, baths, standardSearchProperties)
                }}
              />
            </li>
            <li className="lg-d:hidden">
              <FavoriteToggle
                isActive={favorites ?? false}
                onToggle={() => {
                  routePushFavoritesFilter(!favorites)
                }}
              />
            </li>
          </ul>
        </div>
        <div className="ml-auto mr-16 sm-d:hidden">
          <Dropdown
            title={`${t('ui.sort')}:`}
            items={sortByMethodOptions}
            value={sortByMethod}
            onSelect={(value) => {
              routePushSortMethod(value as SortByState)
              const standardSearchProperties = formatStandardSearchProperties()
              trackSort(value as SortByState, standardSearchProperties)
            }}
            align="end"
            modal={false}
          />
        </div>
        <MobileDialog />
      </div>
    </form>
  )
}

export default FilterBar
